import axios, { AxiosError, AxiosInstance, InternalAxiosRequestConfig } from 'axios'
import { deleteCookie, getCookie, setCookie } from 'cookies-next'
import { NextResponse } from 'next/server'
import queryString from 'query-string'

import { ACCESS_TOKEN_COOKIE, APP_HOST_HEADER, REFRESH_TOKEN_COOKIE, SESSION_ID_HEADER } from '@/constant/app'
import type { TenantConfig } from '@/services/tenant.service'
import { getClientSignInRedirectUrl, isInvalidUserApiError, isProtectedPath, normalizeApiErrorMessage } from '@/utils/auth-api-error'
import { isFsdsHostname } from '@/utils/domain-branding-resolver'
import { getTenantFromHost, isLocalTenantDevelopmentHost, shouldUseJwtAuth } from '@/utils/tenant.utils'

import { IBaseService } from './interfaces/base-service.interface'

import { UnInterceptedApiError } from '@/types/api'

type RetriableRequestConfig = InternalAxiosRequestConfig & { _retry?: boolean }

export class BaseService implements IBaseService {
  private static instance: BaseService
  private readonly axiosClient: AxiosInstance
  private sessionId?: string
  private accessToken?: string
  private refreshToken?: string
  private refreshTokenPromise: Promise<string | null> | null = null
  private isRedirectingToSignIn = false
  private tenantConfigCache: Map<string, { config: TenantConfig; timestamp: number }> = new Map()
  private readonly CACHE_TTL = 5 * 60 * 1000 // 5 minutes
  private currentTenantConfig: TenantConfig | null = null

  private constructor() {
    this.axiosClient = this.createAxiosInstance()
    // Hydrate auth from first-party cookies set by auth-context on prior login.
    if (typeof window !== 'undefined') {
      const at = getCookie(ACCESS_TOKEN_COOKIE)
      const rt = getCookie(REFRESH_TOKEN_COOKIE)
      const sid = getCookie(SESSION_ID_HEADER)
      if (typeof at === 'string') this.accessToken = at
      if (typeof rt === 'string') this.refreshToken = rt
      if (typeof sid === 'string' && sid.trim().length > 0) {
        this.sessionId = sid
        this.axiosClient.defaults.headers[SESSION_ID_HEADER] = sid
      }
    }
    this.setupInterceptors()
  }

  public static getInstance(): BaseService {
    if (!BaseService.instance) {
      BaseService.instance = new BaseService()
    }
    return BaseService.instance
  }

  public getClient(): AxiosInstance {
    return this.axiosClient
  }

  /**
   * Get the tenant ID from the current context
   * Note: For server-side calls, tenant ID should be passed via 'x-tenant-id' header
   * in the request config (see createServerSideApiConfig utility)
   */
  private getTenantId(): string {
    // Server-side: return default (tenant should be passed via x-tenant-id header)
    // The request interceptor will use the header if provided
    if (typeof window === 'undefined') {
      return 'default'
    }

    // Client-side: from cookie or hostname
    const tenantCookie = document.cookie
      .split('; ')
      .find(row => row.startsWith('tenant='))
      ?.split('=')[1]

    if (tenantCookie) {
      return tenantCookie
    }

    // Fallback: extract from hostname
    return getTenantFromHost(window.location.hostname)
  }

  /**
   * Get default/base API URL for initial tenant config fetch
   * This is used before we know the tenant's specific API URL
   */
  private getDefaultBaseUrl(): string {
    // Use NEXT_PUBLIC_API_URL if available
    if (process.env.NEXT_PUBLIC_API_URL) {
      if (typeof window !== 'undefined' && isLocalTenantDevelopmentHost(window.location.hostname)) {
        try {
          const parsedApiUrl = new URL(process.env.NEXT_PUBLIC_API_URL)
          if (parsedApiUrl.hostname === 'localhost') {
            parsedApiUrl.hostname = window.location.hostname
            return parsedApiUrl.toString()
          }
        } catch {
          return process.env.NEXT_PUBLIC_API_URL
        }
      }
      return process.env.NEXT_PUBLIC_API_URL
    }

    // Fallback: construct default API URL
    const domainName = 'fullstackdatascience.com'
    return `https://api.${domainName}`
  }

  /**
   * When the app is hosted on an external domain (e.g. phongth.edunodes.tech),
   * use the main API (api.fullstackdatascience.com). Tenant-prefixed API
   * (tenant.api.fullstackdatascience.com) is only used when the app is on fullstackdatascience.com.
   */
  private isOnMainApiDomain(appHostname?: string | null): boolean {
    const hostname =
      appHostname ?? (typeof window !== 'undefined' ? window.location.hostname : null)
    if (!hostname) {
      return true
    }
    return isFsdsHostname(hostname)
  }

  private getBaseUrlFromStoredConfig(appHostname?: string | null): string {
    const tenantId = this.getTenantId()

    // For default tenant, use default API URL
    if (tenantId === 'default') {
      return this.getDefaultBaseUrl()
    }

    // External domains (e.g. phongth.edunodes.tech) always use main API
    if (!this.isOnMainApiDomain(appHostname)) {
      return this.getDefaultBaseUrl()
    }

    if (this.currentTenantConfig?.subdomain) {
      const domainName = 'fullstackdatascience.com'
      return `https://${this.currentTenantConfig.subdomain}.api.${domainName}`
    }

    const domainName = 'fullstackdatascience.com'
    return `https://${tenantId}.api.${domainName}`
  }

  /**
   * Get default tenant config (no API call)
   * Tenant config should be set externally via setTenantConfig()
   */
  private getDefaultTenantConfig(tenantId: string): TenantConfig {
    return {
      id: tenantId,
      name: tenantId.charAt(0).toUpperCase() + tenantId.slice(1) + ' Tenant',
      subdomain: tenantId,
      theme: {
        primaryColor: '#007bff',
        secondaryColor: '#6c757d',
      },
      features: [],
      status: 'ACTIVE',
    }
  }

  /**
   * Get the base URL for API calls based on tenant config
   * Priority:
   * 1. Stored tenant config subdomain (if available)
   * 2. Fetch and use tenant config subdomain
   * 3. Default NEXT_PUBLIC_API_URL
   */
  private async getBaseUrl(): Promise<string> {
    return this.getBaseUrlForTenant(this.getTenantId())
  }

  /**
   * Get the base URL for a specific tenant ID
   * Tries to avoid fetching tenant config by using cached/stored config
   */
  private async getBaseUrlForTenant(tenantId: string, appHostname?: string | null): Promise<string> {
    // For default tenant, use default API URL
    if (tenantId === 'default') {
      return this.getDefaultBaseUrl()
    }

    // External domains (e.g. phongth.edunodes.tech) always use main API
    if (!this.isOnMainApiDomain(appHostname)) {
      return this.getDefaultBaseUrl()
    }

    // Priority 1: Use stored tenant config if available and matches tenantId
    if (this.currentTenantConfig?.subdomain && this.currentTenantConfig.id === tenantId) {
      const domainName = 'fullstackdatascience.com'
      return `https://${this.currentTenantConfig.subdomain}.api.${domainName}`
    }

    // Priority 2: Check cache (avoid API call if possible)
    const cached = this.tenantConfigCache.get(tenantId)
    if (cached && Date.now() - cached.timestamp < this.CACHE_TTL) {
      const domainName = 'fullstackdatascience.com'
      if (cached.config.subdomain) {
        return `https://${cached.config.subdomain}.api.${domainName}`
      }
    }

    // Priority 3: Try to get from localStorage (client-side only)
    if (typeof window !== 'undefined') {
      try {
        const { getTenantConfigFromStorage } = await import('@/utils/tenant-cache.utils')
        const cachedConfig = getTenantConfigFromStorage(tenantId)
        if (cachedConfig?.subdomain) {
          // Set it in BaseService cache for future use
          this.setTenantConfig(tenantId, cachedConfig)
          const domainName = 'fullstackdatascience.com'
          return `https://${cachedConfig.subdomain}.api.${domainName}`
        }
      } catch {
        // Ignore errors
      }
    }

    // Priority 4: Fallback to default or construct from tenantId
    // DO NOT fetch tenant config here - it should be set externally via setTenantConfig()
    const defaultBaseUrl = this.getDefaultBaseUrl()
    if (defaultBaseUrl !== `https://api.fullstackdatascience.com`) {
      return defaultBaseUrl
    }

    // Final fallback: construct from tenantId (assume tenantId is subdomain)
    const domainName = 'fullstackdatascience.com'
    return `https://${tenantId}.api.${domainName}`
  }

  private createAxiosInstance(): AxiosInstance {
    // Start with base URL from stored tenant config (prioritizes tenant domain)
    // Will be updated dynamically per request if config changes
    const baseURL = this.getBaseUrlFromStoredConfig()

    if (!baseURL) {
      throw new Error('NEXT_PUBLIC_API_URL is not set and could not determine API URL')
    }

    // JWT mode (non-fsds hosts) uses Authorization header; cookies don't reach the API anyway,
    // so withCredentials would only invite third-party cookie blocking by the browser.
    // fsds hosts keep withCredentials: true so the session cookie is sent.
    const isJwtMode = typeof window !== 'undefined' && shouldUseJwtAuth()

    return axios.create({
      baseURL,
      headers: {
        Accept: 'application/json',
        'Content-Type': 'application/json',
        'Access-Control-Allow-Origin': '*',
      },
      withCredentials: !isJwtMode,
      paramsSerializer: params => queryString.stringify(params),
    })
  }

  private setupInterceptors(): void {
    this.setupRequestInterceptor()
    this.setupResponseInterceptor()
  }

  private setupRequestInterceptor(): void {
    this.axiosClient.interceptors.request.use(async config => {
      try {
        // JWT mode (non-fsds hosts): attach Authorization header.
        // fsds mode: rely on the session cookie (sent automatically with withCredentials).
        const isJwtMode = typeof window !== 'undefined' && shouldUseJwtAuth()
        if (isJwtMode && this.accessToken) {
          config.headers['Authorization'] = `Bearer ${this.accessToken}`
        } else if (!isJwtMode && this.sessionId) {
          config.headers[SESSION_ID_HEADER] = this.sessionId
        }

        // Check if tenant ID is provided via custom header (for server-side calls)
        const tenantIdFromHeader = config.headers?.['x-tenant-id'] as string | undefined
        const appHostFromHeader = config.headers?.[APP_HOST_HEADER] as string | undefined

        // Dynamically set baseURL based on tenant config
        // Skip for tenant config endpoint to avoid circular dependency
        const isTenantConfigEndpoint = config.url?.includes('/tenants/') && config.url?.includes('/config')
        if (!isTenantConfigEndpoint) {
          // Use tenant from header if provided (server-side), otherwise use getTenantId()
          const tenantId = tenantIdFromHeader || this.getTenantId()
          const baseURL = await this.getBaseUrlForTenant(tenantId, appHostFromHeader)
          if (baseURL) {
            config.baseURL = baseURL
          }
        }

        // Remove internal headers before sending (they are only for BaseService routing)
        if (config.headers) {
          if (tenantIdFromHeader) delete config.headers['x-tenant-id']
          if (appHostFromHeader) delete config.headers[APP_HOST_HEADER]
        }

        return config
      } catch (error) {
        // eslint-disable-next-line no-console
        console.error('Error in request interceptor:', error)
        return config
      }
    }, error => this.handleRequestError(error))
  }

  private setupResponseInterceptor(): void {
    this.axiosClient.interceptors.response.use(
      response => response,
      async error => {
        if (this.shouldRedirectToSignIn(error)) {
          this.redirectToSignIn()
          return Promise.reject(error)
        }

        const refreshed = await this.tryRefreshAndRetry(error)
        if (refreshed) return refreshed
        return this.handleResponseError(error)
      }
    )
  }

  private shouldRedirectToSignIn(error: AxiosError<UnInterceptedApiError>): boolean {
    if (typeof window === 'undefined' || this.isRedirectingToSignIn) {
      return false
    }

    if (!isInvalidUserApiError(error)) {
      return false
    }

    return isProtectedPath(window.location.pathname)
  }

  private redirectToSignIn(): void {
    if (typeof window === 'undefined' || this.isRedirectingToSignIn) {
      return
    }

    this.isRedirectingToSignIn = true
    deleteCookie(SESSION_ID_HEADER)
    this.clearSessionId()
    this.clearAuthTokens()
    window.location.href = getClientSignInRedirectUrl()
  }

  /**
   * On 401 in JWT mode, exchange the refresh token for a new pair and retry the
   * original request once. Concurrent 401s share a single in-flight refresh.
   * Returns the retried response on success, or null when not applicable.
   */
  private async tryRefreshAndRetry(
    error: AxiosError<UnInterceptedApiError>
  ): Promise<ReturnType<AxiosInstance['request']> | null> {
    if (typeof window === 'undefined') return null
    if (!shouldUseJwtAuth()) return null
    const status = error.response?.status
    if (status !== 401) return null
    const original = error.config as RetriableRequestConfig | undefined
    if (!original || original._retry) return null
    if (!this.refreshToken) return null
    // Don't try to refresh the refresh-token call itself.
    if (typeof original.url === 'string' && original.url.includes('/refresh-token')) return null

    original._retry = true
    const newAccessToken = await this.runRefreshOnce()
    if (!newAccessToken) {
      this.clearAuthTokens()
      return null
    }
    if (!original.headers) original.headers = {} as InternalAxiosRequestConfig['headers']
    original.headers['Authorization'] = `Bearer ${newAccessToken}`
    return this.axiosClient.request(original)
  }

  /**
   * Coalesces concurrent refresh attempts so we only hit /refresh-token once.
   */
  private runRefreshOnce(): Promise<string | null> {
    if (this.refreshTokenPromise) return this.refreshTokenPromise
    this.refreshTokenPromise = this.performRefresh().finally(() => {
      this.refreshTokenPromise = null
    })
    return this.refreshTokenPromise
  }

  private async performRefresh(): Promise<string | null> {
    if (!this.refreshToken) return null
    try {
      const { data } = await this.axiosClient.post<{
        accessToken: string
        refreshToken: string
        accessTokenExpiresIn?: string
        refreshTokenExpiresIn?: string
      }>('/refresh-token', { refreshToken: this.refreshToken })
      this.setAuthTokens({
        accessToken: data.accessToken,
        refreshToken: data.refreshToken,
        accessTokenExpiresIn: data.accessTokenExpiresIn,
        refreshTokenExpiresIn: data.refreshTokenExpiresIn,
      })
      return data.accessToken
    } catch {
      return null
    }
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  private handleRequestError(error: Record<string, any>): Promise<never> {
    const statusCode = error.response?.status

    if (statusCode === 401 || statusCode === 403) {
      // eslint-disable-next-line no-console
      console.log('Unauthorized or Forbidden')
    }

    if (statusCode === 406) {
      deleteCookie(SESSION_ID_HEADER)
      this.axiosClient.post('/logout')
      return Promise.reject(NextResponse.redirect(new URL('/sign-in')))
    }

    return Promise.reject(error)
  }

  private handleResponseError(error: AxiosError<UnInterceptedApiError>): Promise<never> {
    if (error.response?.data.message) {
      const normalizedMessage = normalizeApiErrorMessage(error.response.data.message)
      return Promise.reject({
        ...error,
        response: {
          ...error.response,
          data: {
            ...error.response.data,
            message: normalizedMessage,
          },
        },
      })
    }
    return Promise.reject(error)
  }

  public setSessionId(sessionId: string): void {
    this.sessionId = sessionId
    this.axiosClient.defaults.headers[SESSION_ID_HEADER] = sessionId
  }

  public clearSessionId(): void {
    this.sessionId = undefined
    delete this.axiosClient.defaults.headers[SESSION_ID_HEADER]
  }

  /**
   * Persist JWT tokens in memory + first-party cookie on the FE host so SSR
   * middleware and a fresh page load can both pick them up.
   * Cookies are JS-readable (not HttpOnly) — same risk profile as the existing
   * SESSION_ID_HEADER cookie. See follow-up notes in the JWT auth design doc.
   */
  public setAuthTokens(tokens: {
    accessToken: string
    refreshToken: string
    accessTokenExpiresIn?: string
    refreshTokenExpiresIn?: string
  }): void {
    this.accessToken = tokens.accessToken
    this.refreshToken = tokens.refreshToken
    if (typeof window === 'undefined') return
    const secure = window.location.protocol === 'https:'
    setCookie(ACCESS_TOKEN_COOKIE, tokens.accessToken, {
      path: '/',
      secure,
      sameSite: 'lax',
      maxAge: this.parseExpiresInToSeconds(tokens.accessTokenExpiresIn) ?? 60 * 60 * 24,
    })
    setCookie(REFRESH_TOKEN_COOKIE, tokens.refreshToken, {
      path: '/',
      secure,
      sameSite: 'lax',
      maxAge: this.parseExpiresInToSeconds(tokens.refreshTokenExpiresIn) ?? 60 * 60 * 24 * 30,
    })
  }

  public clearAuthTokens(): void {
    this.accessToken = undefined
    this.refreshToken = undefined
    if (typeof window === 'undefined') return
    deleteCookie(ACCESS_TOKEN_COOKIE, { path: '/' })
    deleteCookie(REFRESH_TOKEN_COOKIE, { path: '/' })
  }

  public hasAccessToken(): boolean {
    return Boolean(this.accessToken)
  }

  public hasSessionCookie(): boolean {
    if (this.sessionId) {
      return true
    }
    if (typeof window === 'undefined') {
      return false
    }
    const fromCookie = getCookie(SESSION_ID_HEADER)
    return typeof fromCookie === 'string' && fromCookie.trim().length > 0
  }

  public getAccessToken(): string | undefined {
    if (this.accessToken) {
      return this.accessToken
    }
    if (typeof window === 'undefined') {
      return undefined
    }
    const fromCookie = getCookie(ACCESS_TOKEN_COOKIE)
    return typeof fromCookie === 'string' ? fromCookie : undefined
  }

  /**
   * Parse JWT expiresIn strings like "1d" / "30d" / "15m" / "604800" into seconds.
   * Returns null when unparseable; caller falls back to a sane default.
   */
  private parseExpiresInToSeconds(input: string | undefined): number | null {
    if (!input) return null
    const trimmed = input.trim()
    const matchSeconds = /^\d+$/.exec(trimmed)
    if (matchSeconds) return parseInt(trimmed, 10)
    const match = /^(\d+)\s*([smhd])$/i.exec(trimmed)
    if (!match) return null
    const value = parseInt(match[1], 10)
    const unit = match[2].toLowerCase()
    const multiplier = unit === 's' ? 1 : unit === 'm' ? 60 : unit === 'h' ? 3600 : 86400
    return value * multiplier
  }

  /**
   * Get the current tenant config from global state
   */
  public getCurrentTenantConfig(): TenantConfig | null {
    return this.currentTenantConfig
  }

  /**
   * Set tenant config externally (from _app.tsx or TenantProvider)
   * This prevents BaseService from fetching tenant config on every API call
   */
  public setTenantConfig(tenantId: string, config: TenantConfig | null): void {
    if (config && config.id === tenantId) {
      this.currentTenantConfig = config
      // Also cache it
      this.tenantConfigCache.set(tenantId, {
        config,
        timestamp: Date.now(),
      })
    } else {
      this.currentTenantConfig = null
    }
  }
}

export const baseService = BaseService.getInstance()
